// Product page extras: mobile sticky add-to-cart bar and personalisation preview.

class StickyAtc extends HTMLElement {
  #observer = null;

  connectedCallback() {
    const target = document.getElementById(this.dataset.target || '');
    if (!target) return;
    this.#observer = new IntersectionObserver(([entry]) => {
      const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this.toggleAttribute('data-visible', scrolledPast);
      this.inert = !scrolledPast;
    });
    this.#observer.observe(target);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
  }
}

class Personalisation extends HTMLElement {
  #input = null;
  #count = null;
  #preview = null;

  connectedCallback() {
    this.#input = this.querySelector('[data-personalisation-input]');
    this.#count = this.querySelector('[data-personalisation-count]');
    this.#preview = this.querySelector('[data-personalisation-preview]');
    if (!this.#input) return;
    this.#input.addEventListener('input', this.#update);
    this.#update();
  }

  disconnectedCallback() {
    this.#input?.removeEventListener('input', this.#update);
  }

  #update = () => {
    const value = this.#input.value;
    const max = this.#input.maxLength > 0 ? this.#input.maxLength : value.length;
    if (this.#count) this.#count.textContent = `${value.length}/${max}`;
    if (this.#preview) {
      this.#preview.textContent = value.trim() || this.#preview.dataset.placeholder || '';
      this.#preview.classList.toggle('opacity-50', !value.trim());
    }
  };
}

if (!customElements.get('s-sticky-atc')) customElements.define('s-sticky-atc', StickyAtc);
if (!customElements.get('s-personalisation')) customElements.define('s-personalisation', Personalisation);

// <s-bundle-builder>: adds this product (current variant + personalisation) and the ticked
// "frequently bought together" items to the cart in one Standard Actions call.
class BundleBuilder extends HTMLElement {
  #ac = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.addEventListener('change', () => this.#update(), opts);
    this.querySelector('[data-bundle-submit]')?.addEventListener('click', () => this.#submit(), opts);
    this.#form()?.addEventListener('change', () => queueMicrotask(() => this.#update()), opts);
    document.addEventListener('variant:update', () => this.#update(), opts);
    this.#observeVariant();
    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  #observer = null;

  // The product-form partial is swapped on variant change, so watch for a new id input.
  #observeVariant() {
    const host = this.#form()?.closest('s-product-form') || document;
    this.#observer = new MutationObserver(() => this.#update());
    this.#observer.observe(host, { childList: true, subtree: true });
  }

  #form() {
    return document.getElementById(this.dataset.formId || '');
  }

  #mainPrices() {
    try {
      return JSON.parse(this.querySelector('[data-main-prices]')?.textContent || '{}');
    } catch {
      return {};
    }
  }

  #mainVariantId() {
    const input = this.#form()?.elements.namedItem('id');
    return input && 'value' in input ? String(input.value) : '';
  }

  #selected() {
    return [...this.querySelectorAll('[data-bundle-item]')].flatMap((row) => {
      const toggle = row.querySelector('[data-bundle-toggle]');
      const variant = row.querySelector('[data-bundle-variant]');
      if (!toggle?.checked || !variant) return [];
      const option = variant instanceof HTMLSelectElement ? variant.selectedOptions[0] : variant;
      return [{ row, addon: row.hasAttribute('data-addon'), id: String(variant.value), title: row.dataset.title || '', label: option?.dataset.label || '', price: Number(option?.dataset.price || 0), compare: Number(option?.dataset.compare || 0) }];
    });
  }

  #update() {
    const mainEntry = this.#mainPrices()[this.#mainVariantId()];
    const mainPrice = Number((Array.isArray(mainEntry) ? mainEntry[0] : mainEntry) ?? 0);
    const mainCompare = Number(Array.isArray(mainEntry) ? mainEntry[1] : 0);
    const mainPriceEl = this.querySelector('[data-main-price] bdi');
    if (mainPriceEl && mainPrice) mainPriceEl.textContent = this.#money(mainPrice);

    for (const row of this.querySelectorAll('[data-bundle-item]')) {
      const variant = row.querySelector('[data-bundle-variant]');
      const option = variant instanceof HTMLSelectElement ? variant.selectedOptions[0] : variant;
      const priceEl = row.querySelector('[data-bundle-price] bdi');
      if (priceEl && option?.dataset.price) priceEl.textContent = this.#money(Number(option.dataset.price));
      row.classList.toggle('opacity-60', !row.querySelector('[data-bundle-toggle]')?.checked);
    }

    const selected = this.#selected();
    // Savings = compare-at savings on every selected item + the bundle discount.
    // Mirrors the "Buy it together" automatic discount: buy any item, get the
    // lowest-priced item tagged bundle-addon at percent off, once per order.
    const percent = Math.min(Math.max(Number(this.dataset.discountPercent || 0), 0), 100);
    const original = Math.max(mainCompare, mainPrice)
      + selected.reduce((sum, item) => sum + Math.max(item.compare, item.price), 0);
    const addons = selected.filter((item) => item.addon && item.price > 0);
    const cheapestAddon = addons.length ? Math.min(...addons.map((item) => item.price)) : 0;
    const bundleDiscount = percent > 0 ? Math.round(cheapestAddon * (percent / 100)) : 0;
    const extras = selected.reduce((sum, item) => sum + item.price, 0) - bundleDiscount;
    const total = mainPrice + extras;
    const savings = original - total;

    const totalEl = this.querySelector('[data-bundle-total] bdi');
    if (totalEl && total) totalEl.textContent = this.#money(total);

    const originalEl = this.querySelector('[data-bundle-original]');
    const savingsEl = this.querySelector('[data-bundle-savings]');
    const showSavings = savings > 0 && total > 0;
    if (originalEl) {
      originalEl.hidden = !showSavings;
      const bdi = originalEl.querySelector('bdi');
      if (bdi) bdi.textContent = showSavings ? this.#money(original) : '';
    }
    if (savingsEl) {
      savingsEl.hidden = !showSavings;
      savingsEl.textContent = showSavings
        ? (this.dataset.savingsLabel || '').replace(/\{\{\s*amount\s*\}\}/g, this.#money(savings))
        : '';
    }

    const count = selected.length + 1;
    const button = this.querySelector('[data-bundle-submit]');
    if (button) {
      const template = count === 1 ? this.dataset.buttonOne : this.dataset.buttonMany;
      button.textContent = (template || '').replace(/\{\{\s*count\s*\}\}/g, String(count));
    }
  }

  #mainAttributes() {
    const form = this.#form();
    if (!form) return [];
    const attributes = [];
    for (const [name, value] of new FormData(form)) {
      const match = String(name).match(/^properties\[(.+)]$/);
      if (match && typeof value === 'string' && value.trim()) attributes.push({ key: match[1], value: value.trim() });
    }
    return attributes;
  }

  async #submit() {
    const button = this.querySelector('[data-bundle-submit]');
    const error = this.querySelector('[data-bundle-error]');
    const mainId = this.#mainVariantId();
    if (!mainId || this.getAttribute('aria-busy') === 'true') return;

    // Kit lines share one visible "Bundle" property listing every item, plus a hidden _bundle_id,
    // so the cart, checkout and order show they were bought together as one kit.
    const selected = this.#selected();
    const bundleAttributes = [];
    if (selected.length) {
      const names = [this.dataset.mainTitle || '', ...selected.map((item) => (item.label && item.label !== 'Default Title' ? `${item.title} (${item.label})` : item.title))]
        .filter(Boolean)
        .join(', ');
      const bundleId = Math.random().toString(36).slice(2, 8).toUpperCase();
      bundleAttributes.push(
        { key: this.dataset.propertyKey || 'Bundle', value: `${this.dataset.bundleTitle || ''} — ${names}` },
        { key: '_bundle_id', value: bundleId }
      );
    }
    const mainLine = { merchandiseId: mainId, quantity: 1 };
    const attributes = [...this.#mainAttributes(), ...bundleAttributes];
    if (attributes.length) mainLine.attributes = attributes;
    const lines = [mainLine, ...selected.map((item) => ({
      merchandiseId: item.id,
      quantity: 1,
      ...(bundleAttributes.length ? { attributes: bundleAttributes } : {}),
    }))];

    this.setAttribute('aria-busy', 'true');
    button?.setAttribute('aria-disabled', 'true');
    button?.classList.add('opacity-50', 'cursor-wait');
    if (error) error.hidden = true;

    try {
      if (window.Shopify?.actions?.updateCart) {
        const result = await window.Shopify.actions.updateCart(
          { lines },
          { event: { context: 'product', detail: { source: 's-bundle-builder' } } }
        );
        const message = result?.userErrors?.[0]?.message;
        if (message) throw new Error(message);
        await window.Shopify.actions.openCart?.();
      } else {
        const root = window.Shopify?.routes?.root || '/';
        const items = lines.map((line) => ({
          id: Number(String(line.merchandiseId).split('/').pop()),
          quantity: 1,
          ...(line.attributes ? { properties: Object.fromEntries(line.attributes.map((a) => [a.key, a.value])) } : {}),
        }));
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!response.ok) throw new Error('');
        window.location.href = `${root}cart`;
      }
    } catch (err) {
      if (error) {
        error.textContent = err?.message || this.dataset.errorMessage || '';
        error.hidden = false;
      }
    } finally {
      this.removeAttribute('aria-busy');
      button?.removeAttribute('aria-disabled');
      button?.classList.remove('opacity-50', 'cursor-wait');
    }
  }

  #money(cents) {
    const format = this.dataset.moneyFormat || '{{amount}}';
    const value = cents / 100;
    const group = (num, decimals, thousands, decimal) => {
      const [whole, frac] = num.toFixed(decimals).split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? `${grouped}${decimal}${frac}` : grouped;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return group(value, 0, ',', '.');
        case 'amount_with_comma_separator': return group(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return group(value, 0, '.', ',');
        case 'amount_with_space_separator': return group(value, 2, ' ', ',');
        case 'amount_with_apostrophe_separator': return group(value, 2, "'", '.');
        default: return group(value, 2, ',', '.');
      }
    });
  }
}

if (!customElements.get('s-bundle-builder')) customElements.define('s-bundle-builder', BundleBuilder);
